import { useState, useEffect } from 'react';
import InstallButton from '../../components/InstallButton';
import StatsCard from './components/StatsCard';
import OccupancyChart from './components/OccupancyChart';
import RecentMovements from './components/RecentMovements';
import ActiveVehicles from './components/ActiveVehicles';
import RevenueChart from './components/RevenueChart';
import SuperAdminDashboard from './SuperAdminDashboard';
import { getCompanyStats, type CompanyStats } from '../../api/stats.api';
import { getCapacity, type CapacityData } from '../../api/spaces.api';
import { formatCOP } from '../../utils/currencyFormat';

export default function Dashboard() {
  const [userName, setUserName] = useState('');
  // Se lee de inmediato: con '' el primer render montaba el dashboard de operación y, para
  // SUPERADMIN, sus componentes pedían movimientos que el backend rechaza con 403.
  const [userRole, setUserRole] = useState(() => localStorage.getItem('userRole') || 'OPERADOR');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [statsData, setStatsData] = useState<CompanyStats | null>(null);
  const [capacityData, setCapacityData] = useState<CapacityData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const name = localStorage.getItem('userName') || 'Usuario';
    const role = localStorage.getItem('userRole') || 'OPERADOR';
    setUserName(name);
    setUserRole(role);

    // Initial fetch
    if (role !== 'SUPERADMIN') {
        fetchData();
    }

    const timer = setInterval(() => {
      setCurrentTime(new Date());
      // Refresh stats every 30s
      if (role !== 'SUPERADMIN') fetchData();
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  const fetchData = async () => {
    try {
        const [stats, capacity] = await Promise.all([
          getCompanyStats(),
          getCapacity()
        ]);
        setStatsData(stats);
        setCapacityData(capacity);
    } catch (error) {
        console.error('Error fetching dashboard data:', error);
    } finally {
        setIsLoading(false);
    }
  };

  // Si es SUPERADMIN, mostrar dashboard específico
  if (userRole === 'SUPERADMIN') {
    return <SuperAdminDashboard />;
  }

  // Calcular valores usando capacidad configurada si existe, sino fallback a stats antiguos
  const activeVehicles = statsData?.activeVehicles ?? 0;
  
  // Usar datos de capacidad si están disponibles (prioridad a configuración)
  const totalSpaces = capacityData?.capacidadTotal ?? (statsData?.totalSpaces ?? 0);
  const freeSpaces = capacityData ? capacityData.disponiblesTotal : (statsData?.freeSpaces ?? 0);
  
  // Porcentaje de ocupación
  const occupancyPercent = capacityData?.porcentajeOcupacion 
    ?? (totalSpaces > 0 ? Math.round((activeVehicles / totalSpaces) * 100) : 0);

  const incomeToday = statsData?.incomeToday ?? 0;
  const activeShifts = statsData?.activeShifts ?? 0;

  // Dashboard para ADMIN y OPERADOR
  const stats = [
    {
      title: 'Vehículos Activos',
      value: activeVehicles.toString(),
      total: totalSpaces > 0 ? `/ ${totalSpaces}` : undefined,
      icon: 'ri-car-line',
      color: 'bg-blue-500',
      trend: `${occupancyPercent}%`,
      trendUp: true,
      isLoading
    },
    {
      title: 'Ingresos Hoy',
      value: formatCOP(incomeToday),
      total: undefined,
      icon: 'ri-money-dollar-circle-line',
      color: 'bg-teal-500',
      trend: 'Hoy',
      trendUp: true,
      isLoading
    },
    {
      title: 'Espacios Libres',
      value: freeSpaces.toString(),
      total: totalSpaces > 0 ? `/ ${totalSpaces}` : undefined,
      icon: 'ri-parking-box-line',
      color: 'bg-green-500',
      trend: 'Disp.',
      trendUp: true,
      isLoading
    },
    {
      title: 'Turnos Activos',
      value: activeShifts.toString(),
      total: undefined,
      icon: 'ri-time-line',
      color: 'bg-purple-500',
      trend: 'En Curso',
      trendUp: true,
      isLoading
    }
  ];



  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">
              Bienvenido, {userName}
            </h1>
            <p className="text-slate-500 mt-1">
              {currentTime.toLocaleDateString('es-ES', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })} - {currentTime.toLocaleTimeString('es-ES')}
            </p>
          </div>
          <div className="flex gap-3 items-center">
             <InstallButton />
             {/* Actions can be added here */}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <StatsCard key={index} {...stat} />
          ))}
        </div>

        {/* Charts and Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <OccupancyChart data={statsData?.charts?.occupancyHistory} />
          </div>
          <div>
            <RevenueChart data={statsData?.charts?.revenueByMethod} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
                 <RecentMovements />
            </div>
             <div>
                <ActiveVehicles />
            </div>
        </div>
      </div>
  );
}
